import { lazy, Suspense, useEffect, useState } from 'react';

import { useDisclosure, useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';

import ModalDelete from '@/components/Modals/ModalDelete';
import Loader from '@/components/Layouts/Loader';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST } from '@/common/constants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { setDataTotalNewInspectingCompany } from '@/redux/slices/notificationSlice';
import { deleteInspector, getListInspector } from '@/services/inspectorService';
import { getNewRecordCounterSuperAdmin } from '@/services/newRecordCounterService';
import SearchAndFilter from './SearchAndFilter';

import '../styles/ListPage.css';

const TableInspector = lazy(() => import('@/components/Tables/TableInspector'));

const InspectorList = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);
    const dispatch = useAppDispatch();

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [dataList, setDataList] = useState([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [statusValue, setStatusValue] = useState('');
    const [typeValue, setTypeValue] = useState('');
    const [totalPageNumber, setTotalPageNumber] = useState(1);

    const showToast = (error: any) => {
        toast({
            title: error?.status || 'Error',
            description: error?.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const handleDelete = (id: string) => {
        setIdToDelete(id);
        onOpen();
    };

    const handleConfirmDelete = async () => {
        const obj = { inspectorId: idToDelete, modifiedBy: name || "System" };
        try {
            const response = await deleteInspector(obj) as any;
            if (response.status === HttpStatusCode.Ok) {
                await getData();
                toast({
                    title: 'Inspecting company deleted.',
                    description: "We've deleted the company for you.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            } else {
                showToast({ code: 'Delete Failed', message: "We failed to delete the company." });
            }
        } catch (error) {
            showToast({ code: 'Delete Error', message: "An error occurred while deleting." });
        } finally {
            onClose();
        }
    };

    const handleSearch = () => {
        setCurrentPageNumber(1);
        setTimeout(() => {
            getData();
        }, 500);
    };

    const setLatestCounter = async () => {
        const responseCounter = await getNewRecordCounterSuperAdmin() as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInspectingCompany(responseCounter?.data?.inspectingCompany || 0));
        }
    }

    const getData = async () => {
        setIsLoading(true);
        const param = { search: searchValue, pageNumber: currentPageNumber, pageSize: currentPageSize, status: statusValue, type: typeValue };

        try {
            const response = await getListInspector(param) as any;
            if (response.status === HttpStatusCode.Ok) {
                setDataList(response.data);
                setTotalPageNumber(response.totalPage);
                setHasNextPage(response.hasNextPage);
                setLatestCounter();
            } else {
                showToast(response);
            }
        } catch (error) {
            showToast({ code: 'Error', message: 'Failed to fetch data' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber]);

    return (
        <>
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <div className="table-container">
                <SearchAndFilter
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    statusValue={statusValue}
                    setStatusValue={setStatusValue}
                    typeValue={typeValue}
                    setTypeValue={setTypeValue}
                    onSearch={handleSearch}
                    onAdd={() => navigate('/manage-inspecting-company/add')}
                />
                <Suspense fallback={<Loader />}>
                    <TableInspector
                        dataList={dataList}
                        isLoading={isLoading}
                        handleEdit={(id: string) => navigate('/manage-inspecting-company/edit', { state: { id } })}
                        handleDelete={handleDelete}
                        handleReview={(id: string) => navigate('/manage-inspecting-company/review', { state: { id } })}
                        currentPageSize={currentPageSize}
                        handleChangePageSize={setCurrentPageSize}
                        handleChangePageNumber={setCurrentPageNumber}
                        currentPageNumber={currentPageNumber}
                        totalPageNumber={totalPageNumber}
                        hasNextPage={hasNextPage}
                    />
                </Suspense>
            </div>
        </>
    );
};

export default InspectorList;
